import { environment } from './../../../../environments/environment';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  inject,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { NavigationExtras, Router } from '@angular/router';
import { FormGroup } from '@angular/forms';
import { firstValueFrom, Observable, Subject, Subscription } from 'rxjs';
import { auditTime, debounceTime, distinctUntilChanged, takeUntil } from 'rxjs/operators';
import type {
  Form,
  FormButton,
  FormField,
  ReactiveDropdownDataSource,
  ReactiveDropdownDataSourceContext,
} from './interfaces/form.interface';
import { DropdownData, InputType, Value } from './interfaces/input.interface';
import { RequestHandler } from '../../../../../src/app/service/OffService/request-handler';
import { FormOperations } from './utils/form-operations';
import { ReactiveFormModelFactory } from './model/reactive-form-model.factory';
import { ReactiveFormModelPatcher, ReactiveFormPatcherContext } from './model/reactive-form-model-patcher';
import { ReactiveFormValueUtils } from './model/reactive-form-value.utils';
import { ReactiveFieldRuntimeStateManager } from './model/reactive-field-runtime-state.manager';
import {
  createReactiveFormChange,
  ReactiveFormChange,
  ReactiveFormChangedField,
  ReactiveFormValue,
} from './interfaces/reactive-form-change.interface';
import { ReactiveTwoFieldsConcatConnector } from './model/reactive-two-fields-concat.connector';
import { RepeatableBindingsUtils } from './model/repeatable-bindings.utils';
import { BrowserDiagnosticsService } from 'src/app/common/diagnostics/browser-diagnostics.service';
import { StorageManager } from 'src/app/common/storage-manager.class';
import { ReactiveDropdownCacheConfig, ReactiveDropdownCacheService } from './utils/reactive-dropdown-cache.service';
import { ReactiveFieldLoadState, ReactiveFieldRuntimeState } from './interfaces/reactive-field-runtime-state.interface';
import { FarmerOnboardingTourService } from 'src/app/service/tour/farmer-onboarding-tour.service';
import { GenericFileUploadService } from 'src/app/common/services/generic-file-upload.service';

type ReactiveDropdownEndpoint = {
  readonly toPromise?: (params?: Record<string, unknown>) => Promise<unknown>;
  readonly perform?: (params?: Record<string, unknown>) => unknown;
  readonly response?: (callback: (value: unknown) => void) => unknown;
  readonly getRequestData?: () => unknown;
  readonly hasError?: boolean;
};

type ReactiveDropdownOptionData = Value<unknown> & Record<string, unknown>;

@Component({
  selector: 'app-reactive-form-builder',
  standalone: false,
  templateUrl: './form-builder.component.html',
  styleUrls: ['./form-builder.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ReactiveFormBuilderComponent<T> implements OnInit, OnDestroy, OnChanges {

  @Input() form!: Form<T>;
  @Input() id = '';
  @Input() previousData: T | null = null;
  @Input() serverUrl = '';

  // IMPORTANT: keep as any to avoid template variance errors (RequestHandler<T[]> -> RequestHandler<unknown> is NOT assignable)
  @Input() request!: RequestHandler<any>;
  @Input() getRequest!: RequestHandler<any>;

  @Input() undoEnabled = false;
  @Input() recordHasAssociatedData = false;
  @Input() areTipsEnabled = false;
  /**
   * Progressive dropdown loading trigger.
   * Parent should preferably pass ReplaySubject(1).
   */
  @Input() dropdownLoadTrigger: Observable<void> | null = null;

  /**
   * True when parent finished loading ALL dropdown lists.
   * Used to decide when "unknown ids" must be cleared.
   */
  @Input() dropdownsFinalized = false;

  @Input() showActionsButtons = true;

  @Input() tourAnchorsEnabled = true;

  @Input() debug = false;

  @Output() formChanges: EventEmitter<ReactiveFormChange> = new EventEmitter<ReactiveFormChange>();

  @Output() goBack: EventEmitter<T> = new EventEmitter<T>();
  @Output() afterSend: EventEmitter<T> = new EventEmitter<T>();
  @Output() hideDialog: EventEmitter<T> = new EventEmitter<T>();

  public appName = environment.appName;
  public filterInputParcelas = environment.features.filterInputParcelas;
  public INPUT = InputType;

  public model!: FormGroup;
  public originalModel: Record<string, string> = {};
  public isLoading = false;
  public fieldStates: ReadonlyMap<string, ReactiveFieldRuntimeState<T>> = new Map();
  public showFieldHelpButtons = false;
  public fieldRenderVersion = 0;

  private static readonly DROPDOWN_TRIGGER_AUDIT_MS = 50;
  private static readonly DEFAULT_DROPDOWN_TIMEOUT_MS = 30000;

  private readonly destroy$ = new Subject<void>();
  private readonly diagnostics = inject(BrowserDiagnosticsService, { optional: true });
  private readonly dropdownCache = inject(ReactiveDropdownCacheService);
  private readonly farmerTourService = inject(FarmerOnboardingTourService);
  private readonly modelFactory = new ReactiveFormModelFactory<T>();
  private readonly modelPatcher = new ReactiveFormModelPatcher<T>();
  private readonly twoFieldsConcatConnector = new ReactiveTwoFieldsConcatConnector<T>();

  private previousModel: ReactiveFormValue<T> = {};
  private formOperations!: FormOperations<T>;
  private dropdownTriggerSub: Subscription | null = null;
  private repeatableBindingControlNames = new Set<string>();
  private declarativeDropdownLoadKeys = new Set<string>();
  private declarativeDropdownActiveLoadKeys = new Map<string, string>();
  private declarativeDropdownLoadVersions = new Map<string, number>();
  private fieldLoadStates = new Map<string, ReactiveFieldLoadState>();
  private fieldErrors = new Map<string, string>();

  private previousDataReady = false;
  private needsDropdowns = false;
  private isBootstrapped = false;
  private isDestroyed = false;

  constructor(
    private http: HttpClient,
    private cdr: ChangeDetectorRef,
    private fileUploadService: GenericFileUploadService,
    private router: Router,
  ) {}

  public toggleFieldHelpButtons(): void {
    this.showFieldHelpButtons = !this.showFieldHelpButtons;
    this.cdr.markForCheck();
  }

  public startCurrentFormTour(): void {
    this.farmerTourService.startCurrentFormTour();
  }

  public getTourAnchorId(anchorId: string): string {
    return this.tourAnchorsEnabled ? anchorId : '';
  }


  public getFormGuidanceTips(): readonly string[] {
    if (this.matchesFormContext(['fincas', 'finca', 'explotación', 'explotacion'])) {
      return [
        'Comprueba el titular o representante de la explotación.',
        'Revisa el código REA o los datos de registro si aparecen.',
        'Confirma teléfono, correo y dirección de contacto antes de guardar.',
      ];
    }

    if (this.matchesFormContext(['parcela', 'parcelas', 'recinto'])) {
      return [
        'Revisa que la finca, el recinto, el cultivo y la superficie coincidan con tu explotación.',
        'Si la parcela viene de REA o SIGPAC, no cambies datos oficiales sin comprobarlos antes.',
        'Usa los sectores si quieres agrupar parcelas que se trabajan igual.',
      ];
    }

    if (this.matchesFormContext(['maquina', 'maquinaria'])) {
      return [
        'Identifica la máquina con un nombre claro: marca, modelo o uso principal.',
        'Revisa ROMA, capacidad, propietario y fechas de inspección o mantenimiento si aparecen.',
        'Adjunta certificados, facturas o informes ITEAF cuando los tengas disponibles.',
      ];
    }
    
    if (this.matchesFormContext(['fitosanitario', 'tratamiento', 'tratamientos'])) {
      return [
        'Prepara fecha, parcela, producto, dosis y problema tratado.',
        'Comprueba el aplicador y, si corresponde, el número ROPO o la máquina utilizada.',
        'Revisa etiqueta o Vademécum antes de guardar dosis y cultivo.',
      ];
    }

    if (this.matchesFormContext(['fertilización', 'fertilizacion', 'fertilizante', 'abonado', 'abono'])) {
      return [
        'Revisa fecha, parcela, tipo de abono, cantidad y método de aplicación.',
        'Distingue si el formulario pide dosis por hectárea o cantidad total.',
        'Si hay fertirrigación, comprueba también los datos del riego.',
      ];
    }

    if (this.matchesFormContext(['maquinaria', 'máquina', 'maquina'])) {
      return [
        'Escribe un nombre claro para reconocer la máquina en las tareas.',
        'Revisa marca, modelo, matrícula o número ROMA si el formulario los pide.',
        'Comprueba que la máquina sea la que realmente se utiliza en campo.',
      ];
    }

    if (this.matchesFormContext(['trabajador', 'aplicador', 'personal'])) {
      return [
        'Comprueba nombre, NIF y datos de contacto de la persona.',
        'Si aplica fitosanitarios, revisa carné o número ROPO cuando corresponda.',
        'Guarda solo trabajadores reales de la explotación o empresa.',
      ];
    }

    if (this.matchesFormContext(['producto', 'productos', 'vademécum', 'vademecum'])) {
      return [
        'Revisa nombre comercial, tipo de producto y uso previsto.',
        'En fitosanitarios, contrasta cultivo, dosis y condiciones de uso con la información oficial.',
        'En fertilizantes, comprueba composición, unidad y forma de aplicación.',
      ];
    }

    return [
      'Revisa los campos obligatorios marcados con asterisco.',
      'Usa el icono azul de cada campo si necesitas saber qué significa o dónde encontrar el dato.',
      'Antes de guardar, comprueba que los datos correspondan a esta pantalla.',
    ];
  }

  public trackByGuidanceTip(index: number, tip: string): string {
    return `${index}-${tip}`;
  }

  private matchesFormContext(keywords: readonly string[]): boolean {
    const formContext = this.normalizeText(`${this.form?.name ?? ''} ${this.form?.header ?? ''}`);

    return keywords.some((keyword) => formContext.includes(this.normalizeText(keyword)));
  }

  private normalizeText(value: string): string {
    return value
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '');
  }


  public ngOnInit(): void {
    this.needsDropdowns = (this.form?.type <= 2 || this.form?.type === 6) && !!this.dropdownLoadTrigger;

    // Render fast: build form immediately and patch progressively.
    this.restoreDeclarativeDropdownCache();
    this.initializeModel();
    this.syncFieldRuntimeStates();
    this.indexRepeatableBindingControls();
    this.subscribeToFormChanges();

    this.originalModel = { ...(this.model.getRawValue() as Record<string, string>) };
    this.formOperations.executeFieldsLogic(null);
    this.saveDeclarativeDropdownCache();
    this.syncFieldRuntimeStates();
    this.isBootstrapped = true;

    // Subscribe ASAP (ReplaySubject in parent avoids missing early emits).
    this.registerDropdownTrigger(this.dropdownLoadTrigger);
    void this.loadInitialDeclarativeDropdowns();

    // If previousData already present from parent (create/duplicate), patch now.
    if (!this.id && this.previousData != null) {
      this.previousDataReady = true;

      this.patchFromPreviousDataPristine();
      this.patchRepeatableBindingsFromPreviousDataPristine();
      this.remapDropdownFieldsPristine();

      this.formOperations.executeFieldsLogic(null);
      this.originalModel = { ...(this.model.getRawValue() as Record<string, string>) };
      this.syncFieldRuntimeStates();
      this.publishSilentModelSnapshot('__previous_data_patch');

      this.updateLoadingState();
      this.cdr?.markForCheck();
    }

    // Edit mode: fetch previousData if id provided.
    if (this.id) {
      this.previousDataReady = false;
      this.updateLoadingState();

      const requestData = this.getRequest.getRequestData();
      const endpoint = String(requestData?.endpoint ?? '');

      const params = endpoint.startsWith('generic/')
        ? { p3: this.id }
        : { id: this.id };

      this.getRequest.perform(params as any);
      this.getRequest.response((res: unknown) => {
        if (!this.getRequest.hasError) {
          const arr = res as unknown as Array<unknown> | undefined;
          const first = Array.isArray(arr) ? arr[0] : null;

          if (first && (first as any).id?.toString() !== this.id?.toString()) {
            return;
          }

          this.previousData = (first as T) ?? null;
          this.previousDataReady = true;

          this.patchFromPreviousDataPristine();
          this.patchRepeatableBindingsFromPreviousDataPristine();
          this.remapDropdownFieldsPristine();

          this.formOperations.executeFieldsLogic(null);
          this.originalModel = { ...(this.model.getRawValue() as Record<string, string>) };
          this.saveDeclarativeDropdownCache();
          this.syncFieldRuntimeStates();
          this.publishSilentModelSnapshot('__previous_data_patch');

          this.updateLoadingState();
          this.cdr?.markForCheck();
        }
      });

      return;
    }

    this.previousDataReady = true;
    this.updateLoadingState();
    this.cdr?.markForCheck();
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if ('dropdownLoadTrigger' in changes) {
      const trigger = changes['dropdownLoadTrigger']?.currentValue as Observable<void> | null;

      this.needsDropdowns = (this.form?.type <= 2 || this.form?.type === 6) && !!trigger;

      if (this.isBootstrapped) {
        this.registerDropdownTrigger(trigger);
      }

      this.syncFieldRuntimeStates();
      this.updateLoadingState();
      this.cdr?.markForCheck();
    }

    if ('dropdownsFinalized' in changes) {
      const nowFinal = Boolean(changes['dropdownsFinalized']?.currentValue);

      if (nowFinal && this.canMutateModelSafely()) {
        this.remapDropdownFieldsPristine();

        this.formOperations.executeFieldsLogic(null);
        this.originalModel = { ...(this.model.getRawValue() as Record<string, string>) };
        this.saveDeclarativeDropdownCache();
        this.syncFieldRuntimeStates();
        this.publishSilentModelSnapshot('__dropdowns_finalized');
      }

      this.syncFieldRuntimeStates();
      this.updateLoadingState();
      this.cdr?.markForCheck();
    }

    if ('previousData' in changes) {
      const current = changes['previousData']?.currentValue as T | null | undefined;

      if (current != null) {
        this.previousDataReady = true;

        if (this.canMutateModelSafely()) {
          this.patchFromPreviousDataPristine();
          this.patchRepeatableBindingsFromPreviousDataPristine();
          this.remapDropdownFieldsPristine();

          this.formOperations.executeFieldsLogic(null);
          this.originalModel = { ...(this.model.getRawValue() as Record<string, string>) };
          this.syncFieldRuntimeStates();
          this.publishSilentModelSnapshot('__previous_data_change');
        }

        this.syncFieldRuntimeStates();
        this.updateLoadingState();
        this.cdr?.markForCheck();
      }
    }
  }

  public ngOnDestroy(): void {
    this.isDestroyed = true;
    this.declarativeDropdownLoadKeys.clear();
    this.declarativeDropdownActiveLoadKeys.clear();

    this.twoFieldsConcatConnector.destroy();
    this.dropdownTriggerSub?.unsubscribe();

    this.destroy$.next();
    this.destroy$.complete();
  }

  public hasMainTableData(): boolean {
    const rows = this.form?.table?.data;

    return Array.isArray(rows) && rows.length > 0;
  }
  
  public returnToMainView(): void {
    this.goBack.emit();
  }

  public getHeaderButtons(): FormButton[] {
    const rootButtons = Array.isArray(this.form?.button)
      ? this.form.button
      : (this.form?.button ? [this.form.button] : []);

    const explicitButtons = Array.isArray(this.form?.buttons)
      ? this.form.buttons
      : [];

    return [...rootButtons, ...explicitButtons].filter((button) => button?.isVisible !== false);
  }

  public executeConfiguredButton(button: FormButton): void {
    if (button.type === 'callback') {
        button.function?.();
        return;
    }

    if (button.type === 'externalLink' && typeof button.href === 'string' && button.href.trim() !== '') {
        window.open(
            button.href,
            button.target ?? '_self',
            button.target === '_blank' ? 'noopener,noreferrer' : undefined
        );
        return;
    }

    if (button.type === 'routerLink' && Array.isArray(button.route) && button.route.length > 0) {
        const navigationExtras: NavigationExtras = {};

        if (button.queryParams !== undefined) {
            navigationExtras.queryParams = button.queryParams;
        }

        if (typeof button.fragment === 'string' && button.fragment.trim() !== '') {
            navigationExtras.fragment = button.fragment;
        }

        void this.router?.navigate(button.route, navigationExtras);
    }
  }

  public executePostSubmissionLogic(res: T): void {
    this.afterSend.emit(res);
  }

  public closeDialog(): void {
    this.hideDialog.emit();
  }

  public async getSignature(event: File): Promise<void> {
    const signatureConfiguration = this.form.signatureConfiguration ?? {
      database: 'tareas_fitosanitarias',
      folder: 'fitosanitarios',
      field: 'signature_img',
      uploadStrategy: 'taskFile',
    };
    const signatureField = signatureConfiguration.field ?? 'signature_img';

    if (signatureConfiguration.uploadStrategy === 'deferredBase64') {
      this.cdr?.markForCheck();
      return;
    }

    this.model.patchValue({ [signatureField]: event });

    const signatureFile = this.model.get(signatureField)?.value as File | null;
    if (signatureFile == null) {
      return;
    }

    const signatureFolder = signatureConfiguration.folder;
    const signatureDatabase = signatureConfiguration.database ?? 'tareas_fitosanitarias';
    const uploadedFileName = await firstValueFrom(
      this.fileUploadService.uploadTaskFile(
        signatureFile,
        signatureFolder,
        signatureDatabase,
        signatureField,
      ),
    );

    if (!uploadedFileName) {
      return;
    }

    this.model.patchValue({
      [signatureField]: uploadedFileName,
    });
    this.cdr?.markForCheck();
  }

  public updateFirma(event: string): void {
    const signatureField = this.form.signatureConfiguration?.field ?? 'signature_img';

    this.model.patchValue({ [signatureField]: event });
    this.cdr?.markForCheck();
  }

  public updateImageModel(event: string, fieldName: string): void {
    this.model.patchValue({ [fieldName]: event });
  }

  public updateForm(data: Record<string, unknown>, emit: boolean): void {
    this.model.patchValue(data, { emitEvent: emit });
    this.cdr?.markForCheck();
  }

  /**
   * Progressive field rendering: ready when list has real options (or all dropdowns finalized).
   * IMPORTANT: if list is empty and finalized, field must still render (empty dropdown, not removed).
   */
  public isFieldReady(field: FormField<T>): boolean {
    return ReactiveFieldRuntimeStateManager.isFieldReady(
      field,
      this.fieldStates,
      this.form.type,
      this.dropdownsFinalized,
    );
  }

  /**
   * Small preview while the field is not ready.
   */
  public getPendingDisplay(field: FormField<T>): string {
    const name = field.name ?? '';

    if (!name) {
      return '';
    }

    const control = this.model?.get(name);

    if (!control) {
      return '';
    }

    return ReactiveFormValueUtils.formatPreviewValue(control.value, field);
  }

  private initializeModel(): void {
    const scope = this.diagnostics?.start('initialize-model', 'reactive-form-builder', this.getDiagnosticsMetadata());

    try {
      this.model = this.modelFactory.create({
        form: this.form,
        previousData: this.previousData,
        dropdownsFinalized: this.dropdownsFinalized,
      });

      this.twoFieldsConcatConnector.bind(this.form, this.model);

      // Create FormOperations AFTER model exists.
      this.formOperations = new FormOperations(this.form, this.model);

      // Sync selected values after model exists.
      this.applyInitialSelectedValues();

      scope?.addMetric('controls', Object.keys(this.model.controls).length, 'items');
      scope?.end();
    } catch (error: unknown) {
      scope?.fail(error);
      throw error;
    }
  }

  private subscribeToFormChanges(): void {
    this.model.valueChanges
      .pipe(
        debounceTime(0),
        distinctUntilChanged((previous, current) => ReactiveFormValueUtils.areModelSnapshotsEquivalent(
          previous as Record<string, unknown>,
          current as Record<string, unknown>,
        )),
        takeUntil(this.destroy$),
      )
      .subscribe((newValuesRaw: unknown) => {
        const newValues = newValuesRaw as ReactiveFormValue<T>;

        if (this.id && !this.previousDataReady) {
          this.previousModel = { ...newValues };
          return;
        }

        const changedFields = Object.keys(newValues).filter(
          (control) => !ReactiveFormValueUtils.areValuesEquivalent(newValues[control], this.previousModel?.[control]),
        );

        if (changedFields.length > 0) {
          for (const changedField of changedFields) {
            if (!this.repeatableBindingControlNames.has(changedField)) {
              this.formOperations.applySelected(
                this.formOperations.getFieldByControlName(changedField),
                newValues[changedField] as T,
              );

              void this.formOperations.executeFieldsLogic(changedField).then(() => {
                this.syncFieldRuntimeStates();
                this.refreshRenderedFields();
              });
            }
          }

          const currentModel = this.model.getRawValue() as ReactiveFormValue<T>;
          const changedField = changedFields[0] as ReactiveFormChangedField<T>;

          this.syncFieldRuntimeStates();
          this.formChanges.emit(createReactiveFormChange<T>(currentModel, changedField));
          void this.loadDependentDeclarativeDropdowns(changedFields);
        }

        this.previousModel = { ...(this.model.getRawValue() as ReactiveFormValue<T>) };
      });
  }

  /**
   * Dropdown trigger can be emitted many times (progressive loading).
   * We remap and debounce to avoid heavy work.
   */
  private registerDropdownTrigger(trigger: Observable<void> | null): void {
    this.dropdownTriggerSub?.unsubscribe();
    this.dropdownTriggerSub = null;

    if (!trigger) {
      return;
    }

    this.dropdownTriggerSub = trigger
      .pipe(
        auditTime(ReactiveFormBuilderComponent.DROPDOWN_TRIGGER_AUDIT_MS),
        takeUntil(this.destroy$),
      )
      .subscribe(() => {
        void this.refreshDropdownMappings();
      });
  }


  private async refreshDropdownMappings(): Promise<void> {
    if (!this.canMutateModelSafely()) {
      return;
    }

    const scope = this.diagnostics?.start('dropdown-trigger-update', 'reactive-form-builder', this.getDiagnosticsMetadata());

    try {
      this.remapDropdownFieldsPristine();

      await this.formOperations.executeFieldsLogic(null);
      this.originalModel = { ...(this.model.getRawValue() as Record<string, string>) };
      this.saveDeclarativeDropdownCache();
      this.syncFieldRuntimeStates();
      this.publishSilentModelSnapshot('__dropdown_refresh');

      this.updateLoadingState();
      this.refreshRenderedFields();
      scope?.end();
    } catch (error: unknown) {
      scope?.fail(error);
      throw error;
    }
  }

  /**
   * Publishes silent internal mutations made with emitEvent: false.
   * Parent components that load dependent dropdown data must see the hydrated model.
   */
  private publishSilentModelSnapshot(changedField: string): void {
    const currentModel = this.model.getRawValue() as ReactiveFormValue<T>;

    this.previousModel = { ...currentModel };
    this.formChanges.emit(createReactiveFormChange<T>(
      currentModel,
      changedField as ReactiveFormChangedField<T>,
    ));
  }

  private updateLoadingState(): void {
    const waitingPrevious = !!this.id && !this.previousDataReady;
    const hasRuntimeStates = this.fieldStates.size > 0;
    const hasLoadingField = hasRuntimeStates && ReactiveFieldRuntimeStateManager.hasLoadingDropdowns(this.fieldStates);
    const waitingLegacyDropdowns = this.needsDropdowns && !hasRuntimeStates && !this.dropdownsFinalized;

    this.isLoading = waitingPrevious || hasLoadingField || waitingLegacyDropdowns;
  }

  private canMutateModelSafely(): boolean {
    return this.isBootstrapped && !!this.form && !!this.model && !!this.formOperations;
  }

  private syncFieldRuntimeStates(): void {
    if (!this.form) {
      this.fieldStates = new Map();
      return;
    }

    this.fieldStates = ReactiveFieldRuntimeStateManager.build(
      this.form,
      this.model ?? null,
      this.dropdownsFinalized,
      this.fieldLoadStates,
      this.fieldErrors,
    );
  }

  private refreshRenderedFields(): void {
    this.fieldRenderVersion += 1;

    if (this.isDestroyed) {
      return;
    }

    this.cdr.detectChanges();
  }

  private restoreDeclarativeDropdownCache(): void {
    this.forEachDeclarativeDropdownField((field, data) => {
      const config = this.getDropdownCacheConfig(field);

      if (!config) {
        return;
      }

      this.dropdownCache.restore(config, data);
    });
  }

  private saveDeclarativeDropdownCache(): void {
    this.forEachDeclarativeDropdownField((field, data) => {
      const config = this.getDropdownCacheConfig(field);

      if (!config) {
        return;
      }

      this.dropdownCache.save(config, data);
    });
  }

  private forEachDeclarativeDropdownField(callback: (field: FormField<T>, data: DropdownData<unknown>) => void): void {
    for (const section of this.form?.sections ?? []) {
      for (const field of section.fields ?? []) {
        const dataSource = field.dataSource;
        const dropdownData = field.input?.data as DropdownData<unknown> | undefined;

        if (!dataSource?.cacheKey || !dropdownData) {
          continue;
        }

        callback(field, dropdownData);
      }
    }
  }

  private getDropdownCacheConfig(field: FormField<T>, changedField: string | null = null): ReactiveDropdownCacheConfig | null {
    const dataSource = field.dataSource;

    if (!dataSource?.cacheKey) {
      return null;
    }

    return {
      namespace: 'reactive-form-builder',
      name: dataSource.cacheKey,
      endpoint: dataSource.endpoint,
      ttlMs: dataSource.ttlMs ?? 300000,
      params: this.resolveDataSourceParams(dataSource, field, changedField),
      userScope: this.getCurrentUserScope(),
      clientScope: this.getCurrentClientScope(),
      appName: this.appName,
    };
  }

  private resolveDataSourceParams(
    dataSource: ReactiveDropdownDataSource<T> | undefined,
    field: FormField<T>,
    changedField: string | null,
  ): Record<string, unknown> {
    const params = dataSource?.params;

    if (typeof params !== 'function') {
      return params ?? {};
    }

    try {
      return params(this.createDataSourceContext(field, changedField));
    } catch (error: unknown) {
      console.warn('[ReactiveFormBuilder] dataSource.params() failed. Cache params fallback to empty object.', error);
      return {};
    }
  }

  private getCurrentUserScope(): string | number {
    try {
      const user = StorageManager.getUser() as { id?: string | number; nombre?: string };

      return user.id ?? user.nombre ?? 'anonymous';
    } catch {
      return 'anonymous';
    }
  }

  private getCurrentClientScope(): string | number {
    try {
      const client = StorageManager.getClient() as { id?: string | number; nombre?: string } | null;

      return client?.id ?? client?.nombre ?? 'all-clients';
    } catch {
      return 'all-clients';
    }
  }

  private async loadInitialDeclarativeDropdowns(): Promise<void> {
    if (!this.canMutateModelSafely()) {
      return;
    }

    await this.loadDeclarativeDropdownFields(
      this.getDeclarativeDropdownFields().filter((field) => this.shouldLoadInitially(field)),
      null,
    );
  }

  private async loadDependentDeclarativeDropdowns(changedFields: readonly string[]): Promise<void> {
    if (!this.canMutateModelSafely() || changedFields.length === 0) {
      return;
    }

    const localFields = this.getDeclarativeDropdownFields()
      .filter((field) => this.shouldFilterLocallyAfterDependencyChange(field, changedFields));

    for (const field of localFields) {
      await this.filterDeclarativeDropdownLocally(field, this.getChangedDependency(field, changedFields));
    }

    const remoteFields = this.getDeclarativeDropdownFields()
      .filter((field) => this.shouldLoadAfterDependencyChange(field, changedFields));

    await Promise.all(remoteFields.map(
      (field) => this.loadDeclarativeDropdownField(field, this.getChangedDependency(field, changedFields)),
    ));
  }

  private async loadDeclarativeDropdownFields(fields: readonly FormField<T>[], changedField: string | null): Promise<void> {
    await Promise.all(fields.map((field) => this.loadDeclarativeDropdownField(field, changedField)));
  }

  private async loadDeclarativeDropdownField(field: FormField<T>, changedField: string | null): Promise<void> {
    const dataSource = field.dataSource;
    const fieldName = field.name ?? '';

    const shouldRequireDependencyValues = field.dependencyBehaviour?.filterLocally !== true;

    if (!dataSource?.endpoint || !fieldName || (shouldRequireDependencyValues && !this.dependenciesHaveValue(field))) {
      return;
    }

    const params = this.resolveDataSourceParams(dataSource, field, changedField);
    const loadKey = this.createDeclarativeDropdownLoadKey(field, params);

    if (this.declarativeDropdownActiveLoadKeys.get(fieldName) === loadKey) {
      return;
    }

    const dropdownData = this.getOrCreateDropdownData(field);
    const config = this.getDropdownCacheConfig(field, changedField);
    const shouldForceRefresh = dataSource.forceReload === true ||
      (changedField !== null && field.dependencyBehaviour?.reloadOnParentChange === true);

    if (!shouldForceRefresh && config && this.dropdownCache.restore(config, dropdownData)) {
      const cachedData = this.applyDependencyFilterToDropdownData(field, dropdownData, changedField);

      this.nextDeclarativeDropdownLoadVersion(fieldName);
      this.setFieldLoadState(fieldName, this.getResolvedDataState(cachedData));
      this.replaceFieldDropdownData(fieldName, cachedData);
      await this.afterDeclarativeDropdownDataChanged(fieldName);
      return;
    }

    const loadVersion = this.nextDeclarativeDropdownLoadVersion(fieldName);

    this.declarativeDropdownLoadKeys.add(loadKey);
    this.declarativeDropdownActiveLoadKeys.set(fieldName, loadKey);
    this.prepareDeclarativeDropdownForLoad(field, changedField);

    try {
      const response = await this.fetchDeclarativeDropdownData(dataSource.endpoint, params, dataSource.timeoutMs);

      if (!this.isDeclarativeDropdownLoadCurrent(fieldName, loadVersion)) {
        return;
      }

      const nextData = this.applyDependencyFilterToDropdownData(
        field,
        this.mapDeclarativeDropdownResponse(field, response, changedField),
        changedField,
      );

      this.replaceFieldDropdownData(fieldName, nextData);
      this.setFieldLoadState(fieldName, this.getResolvedDataState(nextData));

      if (config) {
        this.dropdownCache.save(config, nextData);
      }

      await this.afterDeclarativeDropdownDataChanged(fieldName);
    } catch (error: unknown) {
      if (this.isDeclarativeDropdownLoadCurrent(fieldName, loadVersion)) {
        this.setFieldLoadState(fieldName, 'error', this.toErrorMessage(error));
        this.updateLoadingState();
        this.cdr?.markForCheck();
      }
    } finally {
      this.declarativeDropdownLoadKeys.delete(loadKey);

      const isCurrentActiveLoad = this.isDeclarativeDropdownLoadCurrent(fieldName, loadVersion) &&
        this.declarativeDropdownActiveLoadKeys.get(fieldName) === loadKey;

      if (isCurrentActiveLoad) {
        this.declarativeDropdownActiveLoadKeys.delete(fieldName);
      }
    }
  }

  private async afterDeclarativeDropdownDataChanged(fieldName: string): Promise<void> {
    this.rebuildFormOperations();
    this.remapDropdownFieldsPristine();
    await this.formOperations.executeFieldsLogic(fieldName);
    this.syncFieldRuntimeStates();
    this.updateLoadingState();

    if (!this.id || this.previousDataReady) {
      this.publishSilentModelSnapshot(fieldName);
    }

    this.refreshRenderedFields();
  }

  private prepareDeclarativeDropdownForLoad(field: FormField<T>, changedField: string | null): void {
    const fieldName = field.name ?? '';

    if (!fieldName) {
      return;
    }

    const keepCurrentOptions = changedField !== null && field.dependencyBehaviour?.keepValueUntilOptionsLoaded === true;
    const mustClearControl = changedField !== null && field.dependencyBehaviour?.clearOnParentChange !== false && !keepCurrentOptions;

    this.setFieldLoadState(fieldName, 'loading');

    if (!keepCurrentOptions) {
      const currentData = this.getOrCreateDropdownData(field);

      this.replaceFieldDropdownData(fieldName, {
        ...currentData,
        values: [],
        filtered: [],
        selected: null,
      });
    }

    if (mustClearControl) {
      this.clearDropdownControl(field);
    }

    this.syncFieldRuntimeStates();
    this.updateLoadingState();
    this.cdr?.markForCheck();
  }

  private clearDropdownControl(field: FormField<T>): void {
    const fieldName = field.name ?? '';
    const control = fieldName ? this.model.get(fieldName) : null;

    if (!control) {
      return;
    }

    const emptyValue = ReactiveFormValueUtils.isMultiSelectField(field) ? [] : null;

    this.model.patchValue({ [fieldName]: emptyValue }, { emitEvent: false });
    control.markAsPristine();
    control.markAsUntouched();
  }

  private getDeclarativeDropdownFields(): FormField<T>[] {
    const fields: FormField<T>[] = [];

    for (const section of this.form?.sections ?? []) {
      for (const field of section.fields ?? []) {
        if (field.dataSource?.endpoint) {
          fields.push(field);
        }
      }
    }

    return fields;
  }

  private shouldLoadInitially(field: FormField<T>): boolean {
    if (field.dataSource?.lazy === true) {
      return false;
    }

    if (field.dependencyBehaviour?.filterLocally !== true && !this.dependenciesHaveValue(field)) {
      return false;
    }

    return !ReactiveFormValueUtils.hasRealDropdownOptions(this.getOrCreateDropdownData(field));
  }

  private shouldLoadAfterDependencyChange(field: FormField<T>, changedFields: readonly string[]): boolean {
    const dependsOn = field.dataSource?.dependsOn ?? [];

    if (dependsOn.length === 0 || !dependsOn.some((dependency) => changedFields.includes(dependency))) {
      return false;
    }

    if (field.dependencyBehaviour?.filterLocally === true) {
      return !ReactiveFormValueUtils.hasRealDropdownOptions(this.getOrCreateDropdownData(field)) && this.dependenciesHaveValue(field);
    }

    return field.dependencyBehaviour?.reloadOnParentChange === true && this.dependenciesHaveValue(field);
  }

  private shouldFilterLocallyAfterDependencyChange(field: FormField<T>, changedFields: readonly string[]): boolean {
    const dependsOn = field.dataSource?.dependsOn ?? [];

    return field.dependencyBehaviour?.filterLocally === true &&
      dependsOn.length > 0 &&
      dependsOn.some((dependency) => changedFields.includes(dependency));
  }

  private async filterDeclarativeDropdownLocally(field: FormField<T>, changedField: string | null): Promise<void> {
    const fieldName = field.name ?? '';

    if (!fieldName) {
      return;
    }

    this.nextDeclarativeDropdownLoadVersion(fieldName);
    this.declarativeDropdownActiveLoadKeys.delete(fieldName);

    const currentData = this.getOrCreateDropdownData(field);
    const shouldClearControl = changedField !== null && field.dependencyBehaviour?.clearOnParentChange !== false;
    const nextData = this.applyDependencyFilterToDropdownData(field, {
      ...currentData,
      selected: shouldClearControl ? null : currentData.selected ?? null,
    }, changedField);

    this.replaceFieldDropdownData(fieldName, nextData);
    this.setFieldLoadState(fieldName, this.getResolvedDataState(nextData));

    if (shouldClearControl) {
      this.clearDropdownControl(field);
    }

    await this.afterDeclarativeDropdownDataChanged(fieldName);
  }

  private applyDependencyFilterToDropdownData(
    field: FormField<T>,
    data: DropdownData<unknown>,
    changedField: string | null,
  ): DropdownData<unknown> {
    if (field.dependencyBehaviour?.filterLocally !== true) {
      return data;
    }

    const values = Array.isArray(data.values) ? data.values : [];
    const filtered = this.dependenciesHaveValue(field)
      ? values.filter((option) => this.doesOptionMatchDependencies(field, option, changedField))
      : [];

    return {
      ...data,
      values: [...values],
      filtered,
    };
  }

  private doesOptionMatchDependencies(field: FormField<T>, option: unknown, changedField: string | null): boolean {
    const dataSource = field.dataSource;
    const context = this.createDataSourceContext(field, changedField);

    if (dataSource?.filterOption) {
      return dataSource.filterOption(option as Value<unknown>, context);
    }

    const optionRecord = this.toDependencyComparableRecord(option);

    if (!optionRecord) {
      return true;
    }

    return (dataSource?.dependsOn ?? []).every((dependency) => {
      const parentValue = this.model?.get(dependency)?.value;

      if (!ReactiveFormValueUtils.hasNonEmptyValue(parentValue)) {
        return false;
      }

      if (!(dependency in optionRecord)) {
        return true;
      }

      return this.doesDependencyValueMatch(optionRecord[dependency], parentValue);
    });
  }

  private toDependencyComparableRecord(option: unknown): Record<string, unknown> | null {
    if (option !== null && typeof option === 'object') {
      const optionRecord = option as Record<string, unknown>;
      const value = optionRecord['value'];

      if (value !== null && typeof value === 'object') {
        return value as Record<string, unknown>;
      }

      return optionRecord;
    }

    return null;
  }

  private doesDependencyValueMatch(optionValue: unknown, parentValue: unknown): boolean {
    const optionIds = this.toComparableDependencyIds(optionValue);
    const parentIds = this.toComparableDependencyIds(parentValue);

    if (optionIds.length === 0 || parentIds.length === 0) {
      return false;
    }

    return optionIds.some((optionId) => parentIds.includes(optionId));
  }

  private toComparableDependencyIds(value: unknown): string[] {
    const values = Array.isArray(value) ? value : [value];

    return values
      .map((item) => ReactiveFormValueUtils.extractComparableId(item, 'id') ?? item)
      .filter((item) => ReactiveFormValueUtils.hasNonEmptyValue(item))
      .map((item) => String(item));
  }

  private getChangedDependency(field: FormField<T>, changedFields: readonly string[]): string | null {
    const dependsOn = field.dataSource?.dependsOn ?? [];

    return dependsOn.find((dependency) => changedFields.includes(dependency)) ?? changedFields[0] ?? null;
  }

  private dependenciesHaveValue(field: FormField<T>): boolean {
    const dependsOn = field.dataSource?.dependsOn ?? [];

    if (dependsOn.length === 0) {
      return true;
    }

    return dependsOn.every((dependency) => ReactiveFormValueUtils.hasNonEmptyValue(this.model?.get(dependency)?.value));
  }

  private getOrCreateDropdownData(field: FormField<T>): DropdownData<unknown> {
    const currentData = field.input?.data as DropdownData<unknown> | undefined;

    if (currentData?.values && currentData?.filtered) {
      return currentData;
    }

    return {
      values: [],
      filtered: [],
      selected: null,
      label: currentData?.label ?? '',
    };
  }

  private async fetchDeclarativeDropdownData(
    endpoint: unknown,
    params: Record<string, unknown>,
    timeoutMs: number | undefined,
  ): Promise<unknown> {
    let requestPromise: Promise<unknown>;

    if (typeof endpoint === 'function') {
      requestPromise = Promise.resolve(endpoint(params));
    } else if (typeof endpoint === 'string') {
      requestPromise = firstValueFrom(this.http.get(this.toDataSourceUrl(endpoint), { params: this.toHttpQueryParams(params) }));
    } else {
      requestPromise = this.fetchRequestHandlerDropdownData(endpoint, params);
    }

    return this.withDropdownTimeout(requestPromise, timeoutMs);
  }

  private async fetchRequestHandlerDropdownData(endpoint: unknown, params: Record<string, unknown>): Promise<unknown> {
    const requestEndpoint = endpoint as ReactiveDropdownEndpoint | null;

    if (!requestEndpoint) {
      throw new Error('Unsupported dropdown dataSource endpoint.');
    }

    const toPromiseRequest = requestEndpoint.toPromise;

    if (toPromiseRequest) {
      return toPromiseRequest(params);
    }

    const performRequest = requestEndpoint.perform;
    const registerResponse = requestEndpoint.response;

    if (performRequest && registerResponse) {
      return new Promise<unknown>((resolve, reject): void => {
        registerResponse((response: unknown): void => {
          if (requestEndpoint.hasError === true) {
            reject(new Error('Dropdown request finished with error.'));

            return;
          }

          resolve(response);
        });

        performRequest(params);
      });
    }

    throw new Error('Unsupported dropdown dataSource endpoint.');
  }

  private async withDropdownTimeout(requestPromise: Promise<unknown>, timeoutMs: number | undefined): Promise<unknown> {
    const resolvedTimeout = timeoutMs ?? ReactiveFormBuilderComponent.DEFAULT_DROPDOWN_TIMEOUT_MS;

    if (resolvedTimeout <= 0) {
      return requestPromise;
    }

    return new Promise<unknown>((resolve, reject): void => {
      const timeoutId = globalThis.setTimeout((): void => {
        reject(new Error('Dropdown request timed out.'));
      }, resolvedTimeout);

      requestPromise
        .then((value) => {
          globalThis.clearTimeout(timeoutId);
          resolve(value);
        })
        .catch((error: unknown) => {
          globalThis.clearTimeout(timeoutId);
          reject(error);
        });
    });
  }


  private toDataSourceUrl(endpoint: string): string {
    if (/^https?:\/\//u.test(endpoint)) {
      return endpoint;
    }

    const baseUrl = this.serverUrl || environment.serverUrl || '';
    const separator = baseUrl.endsWith('/') ? '' : '/';

    return `${baseUrl}${separator}ws/${endpoint}`;
  }

  private toHttpQueryParams(params: Record<string, unknown>): Record<string, string> {
    const queryParams: Record<string, string> = {};

    for (const [key, value] of Object.entries(params)) {
      if (value !== null && value !== undefined) {
        queryParams[key] = String(value);
      }
    }

    return queryParams;
  }

  private mapDeclarativeDropdownResponse(
    field: FormField<T>,
    response: unknown,
    changedField: string | null,
  ): DropdownData<unknown> {
    const dataSource = field.dataSource;
    const currentData = this.getOrCreateDropdownData(field);
    const context = this.createDataSourceContext(field, changedField);
    const mappedResponse = dataSource?.mapResponse
      ? dataSource.mapResponse(response, context)
      : this.pickResponsePayload(response, dataSource?.responsePath);

    if (this.isDropdownData(mappedResponse)) {
      const values = this.normalizeDropdownOptions(field, mappedResponse.values, context);
      const filtered = this.normalizeDropdownOptions(field, mappedResponse.filtered?.length ? mappedResponse.filtered : values, context);

      return {
        values,
        filtered,
        selected: mappedResponse.selected ?? currentData.selected ?? null,
        label: mappedResponse.label ?? currentData.label ?? '',
      };
    }

    const values = this.normalizeDropdownOptions(field, mappedResponse, context);

    return {
      values,
      filtered: values,
      selected: currentData.selected ?? null,
      label: currentData.label ?? '',
    };
  }

  private normalizeDropdownOptions(
    field: FormField<T>,
    items: unknown,
    context: ReactiveDropdownDataSourceContext<T>,
  ): ReactiveDropdownOptionData[] {
    const values = Array.isArray(items) ? items : [];

    return values.map((item) => this.mapDropdownOption(field, item, context));
  }

  private mapDropdownOption(
    field: FormField<T>,
    item: unknown,
    context: ReactiveDropdownDataSourceContext<T>,
  ): ReactiveDropdownOptionData {
    const dataSource = field.dataSource;

    if (dataSource?.mapOption) {
      return dataSource.mapOption(item, context) as ReactiveDropdownOptionData;
    }

    if (item !== null && typeof item === 'object') {
      const record = item as Record<string, unknown>;
      const hasNativeOptionShape = 'value' in record && 'label' in record;
      const label = this.resolveDropdownLabel(field, record);
      const value = this.resolveDropdownValue(dataSource, record, item, hasNativeOptionShape);

      return {
        ...this.pickOptionMetadata(record),
        label,
        value,
      };
    }

    return {
      label: String(item ?? ''),
      value: item,
    };
  }

  private resolveDropdownLabel(field: FormField<T>, record: Record<string, unknown>): string {
    const dataSource = field.dataSource;
    const labelKeys = [
      dataSource?.labelKey,
      field.input?.optionLabel,
      'label',
      'nombre',
      'name',
      'razon_social',
      'descripcion',
      'codigo',
      'id',
    ].filter((key): key is string => typeof key === 'string' && key.trim().length > 0);

    for (const key of labelKeys) {
      const value = record[key];

      if (value !== null && value !== undefined && String(value).trim() !== '') {
        return String(value);
      }
    }

    return '';
  }

  private resolveDropdownValue(
    dataSource: ReactiveDropdownDataSource<T> | undefined,
    record: Record<string, unknown>,
    item: unknown,
    hasNativeOptionShape: boolean,
  ): unknown {
    if (dataSource?.valueKey && dataSource.valueKey in record) {
      return record[dataSource.valueKey];
    }

    if (hasNativeOptionShape) {
      return record['value'];
    }

    return item;
  }

  private pickOptionMetadata(record: Record<string, unknown>): Record<string, unknown> {
    const metadata: Record<string, unknown> = {};

    for (const [key, value] of Object.entries(record)) {
      if (key === 'value' || key === 'label') {
        continue;
      }

      if (value === null || ['string', 'number', 'boolean'].includes(typeof value)) {
        metadata[key] = value;
      }
    }

    return metadata;
  }

  private pickResponsePayload(response: unknown, responsePath: string | undefined): unknown {
    if (responsePath) {
      return this.readResponsePath(response, responsePath);
    }

    if (Array.isArray(response)) {
      return response;
    }

    if (response !== null && typeof response === 'object') {
      const record = response as Record<string, unknown>;

      if (Array.isArray(record['data'])) {
        return record['data'];
      }

      if (Array.isArray(record['values'])) {
        return record['values'];
      }

      if (Array.isArray(record['items'])) {
        return record['items'];
      }

      if (Array.isArray(record['result'])) {
        return record['result'];
      }
    }

    return [];
  }

  private readResponsePath(response: unknown, responsePath: string): unknown {
    return responsePath
      .split('.')
      .reduce<unknown>((current, key) => {
        if (current !== null && typeof current === 'object') {
          return (current as Record<string, unknown>)[key];
        }

        return undefined;
      }, response);
  }

  private isDropdownData(value: unknown): value is DropdownData<unknown> {
    if (value === null || typeof value !== 'object') {
      return false;
    }

    const record = value as Partial<DropdownData<unknown>>;

    return Array.isArray(record.values) && Array.isArray(record.filtered);
  }

  private replaceFieldDropdownData(fieldName: string, data: DropdownData<unknown>): void {
    this.form = {
      ...this.form,
      sections: (this.form.sections ?? []).map((section) => ({
        ...section,
        fields: (section.fields ?? []).map((field) => {
          if (field.name !== fieldName) {
            return field;
          }

          return {
            ...field,
            input: {
              ...field.input,
              data,
            },
          };
        }),
      })),
    };
  }

  private rebuildFormOperations(): void {
    if (!this.model || !this.form) {
      return;
    }

    this.formOperations = new FormOperations(this.form, this.model);
    this.indexRepeatableBindingControls();
  }

  private setFieldLoadState(fieldName: string, loadState: ReactiveFieldLoadState, errorMessage: string | null = null): void {
    this.fieldLoadStates.set(fieldName, loadState);

    if (errorMessage) {
      this.fieldErrors.set(fieldName, errorMessage);
    } else {
      this.fieldErrors.delete(fieldName);
    }

    this.syncFieldRuntimeStates();
  }

  private getResolvedDataState(data: DropdownData<unknown>): ReactiveFieldLoadState {
    return ReactiveFormValueUtils.hasRealDropdownOptions(data) ? 'ready' : 'empty';
  }

  private createDeclarativeDropdownLoadKey(field: FormField<T>, params: Record<string, unknown>): string {
    return [field.name ?? '', field.dataSource?.cacheKey ?? '', JSON.stringify(params)].join(':');
  }

  private nextDeclarativeDropdownLoadVersion(fieldName: string): number {
    const nextVersion = (this.declarativeDropdownLoadVersions.get(fieldName) ?? 0) + 1;

    this.declarativeDropdownLoadVersions.set(fieldName, nextVersion);

    return nextVersion;
  }

  private isDeclarativeDropdownLoadCurrent(fieldName: string, loadVersion: number): boolean {
    return !this.isDestroyed && this.declarativeDropdownLoadVersions.get(fieldName) === loadVersion;
  }

  private createDataSourceContext(field: FormField<T>, changedField: string | null): ReactiveDropdownDataSourceContext<T> {
    return {
      field,
      model: this.model ?? null,
      previousData: this.previousData,
      changedField,
    };
  }

  private toErrorMessage(error: unknown): string {
    const technicalMessage = error instanceof Error ? error.message : String(error ?? '');
    const trimmedMessage = technicalMessage.trim();

    if (trimmedMessage !== '') {
      return `No hemos podido cargar este dato. Revisa la conexión o inténtalo de nuevo. Detalle: ${trimmedMessage}`;
    }

    return 'No hemos podido cargar las opciones del desplegable. Revisa la conexión o inténtalo de nuevo.';
  }

  private getPatcherContext(): ReactiveFormPatcherContext<T> {
    return {
      form: this.form,
      model: this.model,
      previousData: this.previousData,
      dropdownsFinalized: this.dropdownsFinalized,
      formOperations: this.formOperations,
    };
  }

  private patchFromPreviousDataPristine(): void {
    const scope = this.diagnostics?.start('patch-from-previous-data-pristine', 'reactive-form-builder', this.getDiagnosticsMetadata());

    try {
      this.modelPatcher.patchFromPreviousDataPristine(this.getPatcherContext());
      scope?.end();
    } catch (error: unknown) {
      scope?.fail(error);
      throw error;
    }
  }

  private remapDropdownFieldsPristine(): void {
    const scope = this.diagnostics?.start('remap-dropdown-fields-pristine', 'reactive-form-builder', this.getDiagnosticsMetadata());

    try {
      this.modelPatcher.remapDropdownFieldsPristine(this.getPatcherContext());
      scope?.end();
    } catch (error: unknown) {
      scope?.fail(error);
      throw error;
    }
  }

  private applyInitialSelectedValues(): void {
    this.modelPatcher.applyInitialSelectedValues(this.getPatcherContext());
  }

  private indexRepeatableBindingControls(): void {
    this.repeatableBindingControlNames = RepeatableBindingsUtils.index(this.form);
  }

  private patchRepeatableBindingsFromPreviousDataPristine(): void {
    const scope = this.diagnostics?.start('patch-repeatable-bindings', 'reactive-form-builder', this.getDiagnosticsMetadata());

    try {
      RepeatableBindingsUtils.patchFromPreviousDataPristine(this.form, this.model, this.previousData);
      scope?.end();
    } catch (error: unknown) {
      scope?.fail(error);
      throw error;
    }
  }

  private getDiagnosticsMetadata(): Record<string, unknown> {
    return {
      formName: String(this.form?.name ?? ''),
      formType: this.form?.type ?? null,
      hasId: Boolean(this.id),
      dropdownsFinalized: this.dropdownsFinalized,
    };
  }
}
